import { useCallback, useEffect, useMemo, useState } from 'react'
import { controlRun, getJourneys, getRuns, startRun } from '@/lib/api'
import { isUnauthorized } from '@/hooks/useSession'
import type { Journey, Run, StartRunRequest } from '@/types/journey'

interface JourneyListState {
  journeys: Journey[]
  total: number
  loading: boolean
  error: string | null
}

/**
 * The organisation's journeys, read through the proxy.
 *
 * Those belonging to the current place come first rather than being filtered to
 * it: `place_id` is an optional, unvalidated tag on the geo API's own admission
 * — "staff sometimes mis-map them" — so hiding the rest would hide real work.
 */
export function useJourneyList(placeId: string | null): JourneyListState & { reload: () => void } {
  const [state, setState] = useState<JourneyListState>({
    journeys: [],
    total: 0,
    loading: true,
    error: null,
  })
  const [nonce, setNonce] = useState(0)
  const reload = useCallback(() => setNonce((value) => value + 1), [])

  useEffect(() => {
    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))

    getJourneys()
      .then(({ journeys, total }) => {
        if (cancelled) return
        setState({ journeys, total, loading: false, error: null })
      })
      .catch((error: unknown) => {
        if (cancelled || isUnauthorized(error)) return
        setState({
          journeys: [],
          total: 0,
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        })
      })

    return () => {
      cancelled = true
    }
  }, [nonce])

  const ordered = useMemo(() => {
    if (!placeId) return state.journeys
    return [...state.journeys].sort((a, b) => {
      const aHere = a.place_id === placeId ? 0 : 1
      const bHere = b.place_id === placeId ? 0 : 1
      if (aHere !== bHere) return aHere - bHere
      return (a.name ?? '').localeCompare(b.name ?? '')
    })
  }, [state.journeys, placeId])

  return { ...state, journeys: ordered, reload }
}

interface RunsState {
  runs: Run[]
  error: string | null
  busy: boolean
}

/**
 * The organisation's runs.
 *
 * Its own slice rather than a corner of the socket state: runs outlive the
 * dots they produce, and a terminal run is still worth showing. The socket
 * delivers transitions, and every `snapshot` triggers a refetch — a lagging
 * viewer is resynced with a snapshot *only*, so it may have missed the event
 * that said a run had failed.
 */
export function useJourneyRuns(runEvent: Run | null, snapshotNonce: number) {
  const [state, setState] = useState<RunsState>({ runs: [], error: null, busy: false })

  const refetch = useCallback(() => {
    getRuns()
      .then(({ runs }) => setState((prev) => ({ ...prev, runs })))
      .catch((error: unknown) => {
        if (isUnauthorized(error)) return
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : String(error),
        }))
      })
  }, [])

  useEffect(() => refetch(), [refetch, snapshotNonce])

  // Fold a transition in immediately, so play/pause feels instant rather than
  // waiting for a round trip that has already happened.
  useEffect(() => {
    if (!runEvent) return
    setState((prev) => {
      const runs = prev.runs.filter((run) => run.id !== runEvent.id)
      return { ...prev, runs: [runEvent, ...runs] }
    })
  }, [runEvent])

  const apply = useCallback((run: Run) => {
    setState((prev) => ({
      ...prev,
      busy: false,
      error: null,
      runs: [run, ...prev.runs.filter((existing) => existing.id !== run.id)],
    }))
  }, [])

  const fail = useCallback((error: unknown) => {
    if (isUnauthorized(error)) return
    setState((prev) => ({
      ...prev,
      busy: false,
      error: error instanceof Error ? error.message : String(error),
    }))
  }, [])

  const start = useCallback(
    (request: StartRunRequest) => {
      setState((prev) => ({ ...prev, busy: true, error: null }))
      startRun(request).then(apply).catch(fail)
    },
    [apply, fail],
  )

  const control = useCallback(
    (runId: string, action: 'pause' | 'resume' | 'stop') => {
      setState((prev) => ({ ...prev, busy: true, error: null }))
      controlRun(runId, action).then(apply).catch(fail)
    },
    [apply, fail],
  )

  const clearError = useCallback(() => setState((prev) => ({ ...prev, error: null })), [])

  return { ...state, start, control, refetch, clearError }
}
